import { useEffect, useRef, useState } from "react";

/**
 * Mobile controls: one transparent joystick on the left, one action button on
 * the right. Desktop hides this with CSS.
 */
interface Props {
  onMove: (x: number, y: number) => void;
  onAction: () => void;
}

export function TouchControls({ onMove, onAction }: Props) {
  const joyRef = useRef<HTMLDivElement>(null);
  const activePointer = useRef<number | null>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const stop = (e: TouchEvent | PointerEvent) => {
      e.stopPropagation();
    };
    const root = document.querySelector<HTMLElement>(".touch-controls");
    if (!root) return;
    root.addEventListener("touchstart", stop, { passive: true });
    root.addEventListener("pointerdown", stop, { passive: true });
    return () => {
      root.removeEventListener("touchstart", stop);
      root.removeEventListener("pointerdown", stop);
    };
  }, []);

  const updateJoystick = (clientX: number, clientY: number) => {
    const el = joyRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const max = rect.width * 0.34;
    const dx = clientX - cx;
    const dy = clientY - cy;
    const dist = Math.hypot(dx, dy);
    const scale = dist > max ? max / dist : 1;
    const kx = dx * scale;
    const ky = dy * scale;
    setKnob({ x: kx, y: ky });
    onMove(kx / max, -ky / max);
  };

  const releaseJoystick = () => {
    activePointer.current = null;
    setKnob({ x: 0, y: 0 });
    onMove(0, 0);
  };

  return (
    <div className="touch-controls">
      <div
        ref={joyRef}
        className="touch-joystick"
        onPointerDown={(e) => {
          e.preventDefault();
          activePointer.current = e.pointerId;
          e.currentTarget.setPointerCapture(e.pointerId);
          updateJoystick(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (activePointer.current !== e.pointerId) return;
          e.preventDefault();
          updateJoystick(e.clientX, e.clientY);
        }}
        onPointerUp={(e) => {
          if (activePointer.current !== e.pointerId) return;
          e.preventDefault();
          releaseJoystick();
        }}
        onPointerCancel={releaseJoystick}
        aria-label="Move"
      >
        <span
          className="touch-joystick-knob"
          style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }}
        />
      </div>
      <div className="touch-controls-right">
        <button
          className="touch-action"
          onPointerDown={(e) => {
            e.preventDefault();
            onAction();
          }}
          aria-label="Jump"
        >
          <span>↑</span>
        </button>
      </div>
    </div>
  );
}
